import React, { useCallback, useEffect, useRef, useState } from 'react';

import PLACEHOLDER_IMG from '../utils/placeholderImage';

/**
 * Full-screen listing-image lightbox (#1144).
 *
 * Replaces the old "open the raw image URL in a new browser tab"
 * dead-end. Clicking a listing photo used to `window.open(...)` the
 * bare image, leaving the user on a tab with nothing to do but hit
 * back. This in-app modal instead lets them page through the whole
 * photo set with the on-screen arrows or the keyboard (Left / Right),
 * wraps around at the ends ("take me back through the images"), and
 * closes on Escape, a backdrop click, or the X.
 *
 * Hand-rolled to match FlagDataModal / ReportViewer — the app
 * deliberately avoids a modal/lightbox dependency (see FlagDataModal
 * for the same reasoning). lucide-react is not a project dependency,
 * so the chevrons ship as small inline SVGs.
 *
 * Props:
 *   photos          array of image URLs (the same array the gallery
 *                   renders from — listingMedia.photo_urls)
 *   startIndex      index of the photo to open on
 *   onClose         called to dismiss the lightbox
 *   onNavigate      optional; called with the newly-shown URL each
 *                   time the user pages, so the parent gallery can
 *                   stay in sync and land on that photo when closed
 *   returnFocusRef  ref to the element that opened the lightbox;
 *                   focus is restored to it on close
 */

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function ChevronLeftIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden="true" {...ICON_STROKE}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden="true" {...ICON_STROKE}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" {...ICON_STROKE}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// Circular control button shared by the prev / next / close controls.
const controlButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  borderRadius: 8,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  cursor: 'pointer',
  padding: 0,
  letterSpacing: 0,
};

export default function ListingImageLightbox({
  photos,
  startIndex = 0,
  onClose,
  onNavigate,
  returnFocusRef,
}) {
  const n = Array.isArray(photos) ? photos.length : 0;
  const [idx, setIdx] = useState(() => {
    if (n === 0) return 0;
    const s = Number.isInteger(startIndex) ? startIndex : 0;
    return ((s % n) + n) % n;
  });

  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Keep the latest callbacks in refs so the mount-only effect below
  // (body-scroll lock + focus management) never re-runs just because
  // the parent passed a fresh onClose / returnFocusRef identity — that
  // would steal focus back to the close button mid-navigation.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusRefRef = useRef(returnFocusRef);
  returnFocusRefRef.current = returnFocusRef;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  const goTo = useCallback(
    (next) => {
      setIdx((prev) => {
        if (n === 0) return prev;
        const wrapped = ((next % n) + n) % n;
        if (onNavigateRef.current) onNavigateRef.current(photos[wrapped], wrapped);
        return wrapped;
      });
    },
    [n, photos],
  );

  const goPrev = useCallback(() => goTo(idx - 1), [goTo, idx]);
  const goNext = useCallback(() => goTo(idx + 1), [goTo, idx]);

  // Mount-only: lock body scroll, move focus into the dialog, and on
  // unmount restore both the scroll position and focus to the trigger.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const toFocus = closeBtnRef.current || dialogRef.current;
    if (toFocus && typeof toFocus.focus === 'function') toFocus.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      const rf = returnFocusRefRef.current && returnFocusRefRef.current.current;
      if (rf && typeof rf.focus === 'function') rf.focus();
    };
  }, []);

  // Keyboard: Left / Right page, Escape closes, Tab is trapped inside
  // the dialog's focusable controls.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onCloseRef.current) onCloseRef.current();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = Array.from(root.querySelectorAll('button')).filter(
          (el) => !el.disabled,
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  if (n === 0) return null;

  const currentUrl = photos[idx];
  const multi = n > 1;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Listing photo viewer"
      data-testid="listing-image-lightbox"
      tabIndex={-1}
      onClick={() => onCloseRef.current && onCloseRef.current()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 5000,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4vh 4vw',
      }}
    >
      <button
        ref={closeBtnRef}
        type="button"
        aria-label="Close photo viewer"
        data-testid="listing-image-lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          if (onCloseRef.current) onCloseRef.current();
        }}
        style={{
          ...controlButtonStyle,
          position: 'absolute',
          top: 14,
          right: 14,
          width: 40,
          height: 40,
        }}
      >
        <CloseIcon />
      </button>

      {multi && (
        <button
          type="button"
          aria-label="Previous image"
          data-testid="listing-image-lightbox-prev"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          style={{
            ...controlButtonStyle,
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 52,
            height: 52,
          }}
        >
          <ChevronLeftIcon />
        </button>
      )}

      {/* The photo itself. stopPropagation so clicking the image does
          not fall through to the backdrop close handler. */}
      <img
        src={currentUrl}
        alt={`Listing photo ${idx + 1} of ${n}`}
        onClick={(e) => e.stopPropagation()}
        onError={(e) => {
          // Mirror the inline-gallery behaviour (#1018): swap a photo
          // that 404s / times out for the shared SVG placeholder
          // exactly once, guarding against a reload loop.
          if (e.currentTarget.getAttribute('data-fallback')) return;
          e.currentTarget.setAttribute('data-fallback', '1');
          e.currentTarget.src = PLACEHOLDER_IMG;
        }}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
          borderRadius: 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        }}
      />

      {multi && (
        <button
          type="button"
          aria-label="Next image"
          data-testid="listing-image-lightbox-next"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          style={{
            ...controlButtonStyle,
            position: 'absolute',
            right: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 52,
            height: 52,
          }}
        >
          <ChevronRightIcon />
        </button>
      )}

      <div
        data-testid="listing-image-lightbox-counter"
        aria-live="polite"
        style={{
          position: 'absolute',
          bottom: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          padding: '4px 12px',
          borderRadius: 8,
          fontSize: '0.85rem',
          letterSpacing: 0,
        }}
      >
        {idx + 1} / {n}
      </div>
    </div>
  );
}
